import React, { useCallback, useMemo } from "react";

import { CrossBorderNote } from "@bcc/components/products/cross-border/cross-border-note";
import { CrossBorderBreakdown } from "@bcc/components/products/cross-border/price-breakdown";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { NumericInput } from "@bcc/components/ui/numeric-input";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Textarea } from "@bcc/components/ui/textarea";
import { useToast } from "@bcc/hooks/use-toast";
import {
  breakdownsToSkuPrices,
  calculateMarginPercent,
  computeRegionPriceBreakdowns,
  DEFAULT_TAX_RATES_BY_REGION,
  displayWholesaleForRegion,
  parseBagSizeToKg,
  resolveHomeRegionPricingContext,
  roundMoney,
  vatRateToPercent,
  type RegionPriceBreakdowns,
  type TaxRatesByRegion,
} from "@bcc/lib/products/cross-border-tax";
import { buildShipToCurrentValues } from "@bcc/lib/products/ship-to-countries";
import { SessionEndedError } from "@client-shared/providers/session-ended-error";
import { zodResolver } from "@hookform/resolvers/zod";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import type { ApprovalRequestResult, CountryApprovalValues, RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import type { ShipToInfo } from "@shared/types";
import { format } from "date-fns";
import type { TFunction } from "i18next";
import { CalendarIcon, Loader2 } from "lucide-react";
import type { Control, UseFormReturn } from "react-hook-form";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApprovalRequestResults } from "./approval-request-results";
import { setPriceUpdatePopoverTestId } from "./price-update-popover.utils";
import { CountryFlag } from "./products-table/cells/CountryFlag";

export interface PriceUpdateVariant {
  id: string;
  size: string;
  sku: string;
  vendorKey: string;
  productName: string;
  coffeeTerm: string;
  msrp: number;
  wholesalePrice: number;
  // The roaster's own ISO country code (e.g. "DE", "NL", "US"). Becomes the
  // request's roasterCountryCode and the shipToCountryCode of the single
  // ship-to entry for non-cross-border SKUs.
  roasterCountryCode: string;
  // Current weekly inventory for the SKU. Required by the backend on every
  // price-update ship-to entry so the product isn't left stuck mid-inventory
  // without a matching price-update approval.
  weeklyInventory?: number;
  // Per-ship-to-country current values from the product fetch (channel → country).
  // When present, drives per-country currentMSRP / currentWholesalePrice; absent
  // for non-cross-border SKUs, which fall back to the single msrp/wholesalePrice.
  shipToInfo?: ShipToInfo[];
  // Cross-border ship-to countries (e.g. ["NL", "DE"]). When more than one is
  // present the popover renders the cross-border layout: the home region is
  // editable (bruto entry + KS/VAT breakdown) and every other region is
  // auto-derived and read-only. Absent or single-entry → the single-region
  // price form (unchanged).
  shipTo?: CrossBorderCountries[];
  // The roaster's home region — the editable region in the cross-border layout.
  // Defaults to the first ship-to entry when absent.
  homeRegion?: CrossBorderCountries;
}

interface PriceUpdatePopoverProps {
  variant: PriceUpdateVariant;
  onUpdatePrice: (request: RoasterApprovalRequestDraft) => Promise<ApprovalRequestResult[]>;
  isUpdating?: boolean;
  children: React.ReactNode;
  testId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFormReady?: (form: PriceUpdateForm) => void;
  // Per-region VAT/KS rates (from useTaxRates). Defaults to the fallback rates
  // so the component renders correctly in isolation (stories/tests).
  ratesByRegion?: TaxRatesByRegion;
  // When set, the wholesale price is validated against this margin floor.
  // margin = (msrp - wholesalePrice) / msrp * 100; must be >= marginFloorPercent.
  marginFloorPercent?: number;
}

// Cutoff day of month for price-change effective dates. Submissions made
// on or before this day take effect on the 1st of the NEXT month;
// submissions made after roll forward an extra month, since there isn't
// enough lead time to clear approval before the upcoming 1st.
const PRICE_CHANGE_CUTOFF_DAY = 15;

const getNextValidEffectiveDate = (fromDate: Date): Date => {
  const next = new Date(fromDate);
  next.setHours(0, 0, 0, 0);
  const monthOffset = next.getDate() <= PRICE_CHANGE_CUTOFF_DAY ? 1 : 2;
  next.setMonth(next.getMonth() + monthOffset, 1);
  return next;
};

const isSameCalendarDate = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();

const createSchema = (t: TFunction, validEffectiveDate: Date, marginFloorPercent?: number) => {
  const priceMsg = t("products.priceUpdate.validation.priceRequired");
  const priceField = z
    .number({ error: priceMsg })
    .finite(priceMsg)
    .positive(priceMsg)
    .nullable()
    .refine((value) => value !== null, priceMsg);
  return z
    .object({
      msrp: priceField,
      wholesalePrice: priceField,
      effectiveDate: z
        .date()
        .refine((date) => isSameCalendarDate(date, validEffectiveDate), t("products.priceUpdate.validation.firstOfMonthRequired")),
      notes: z.string().trim().min(1, t("products.priceUpdate.validation.notesRequired")),
    })
    .superRefine((data, ctx) => {
      if (!marginFloorPercent || marginFloorPercent <= 0) return;
      const msrp = data.msrp ?? 0;
      const wholesale = data.wholesalePrice ?? 0;
      if (msrp > 0) {
        const margin = calculateMarginPercent(msrp, wholesale);
        if (margin < marginFloorPercent) {
          ctx.addIssue({
            code: "custom",
            message: t("products.priceUpdate.validation.marginTooLow", { percent: marginFloorPercent, current: margin.toFixed(1) }),
            path: ["wholesalePrice"],
          });
        }
      }
    });
};

type PriceUpdateSchema = ReturnType<typeof createSchema>;
type PriceUpdateFormInputValues = z.input<PriceUpdateSchema>;
export type PriceUpdateFormValues = z.output<PriceUpdateSchema>;
export type PriceUpdateForm = UseFormReturn<PriceUpdateFormInputValues, unknown, PriceUpdateFormValues>;

type PriceFieldName = "msrp" | "wholesalePrice";

const asNumber = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0);
const formatMoney = (value: number): string => value.toFixed(2);

// One bruto price input bound to the form, used for both the single-region
// layout and the editable home region in the cross-border layout. Children
// render an optional tax breakdown beneath the field.
interface PriceInputProps {
  control: Control<PriceUpdateFormInputValues, unknown, PriceUpdateFormValues>;
  name: PriceFieldName;
  label: string;
  error?: string;
  hasMarginError?: boolean;
  children?: React.ReactNode;
}

const PriceInput = ({ control, name, label, error, hasMarginError, children }: PriceInputProps) => (
  <div className="space-y-2">
    <Label htmlFor={name} className="text-xs">
      {label}
    </Label>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <NumericInput
          id={name}
          testId={setPriceUpdatePopoverTestId(`input-${name}`)}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          name={field.name}
          aria-invalid={hasMarginError || undefined}
        />
      )}
    />
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

// Current cross-border prices are always treated as gross (VAT + KS inclusive):
// the roaster types the final bruto and the net/KS/VAT split is shown read-only.
const GROSS_FLAGS = { vatIncluded: true, ksIncluded: true } as const;

// Cross-border destinations (DE/NL) are EUR-only, so the KS rate badge renders
// the amount with a euro prefix.
const formatEuro = (value: number): string => `€${formatMoney(value)}`;

const countryName = (t: TFunction, region: CrossBorderCountries): string =>
  t(`views.productsTable.shipTo.countries.${region}`, { defaultValue: region });

// Auto-calculated, read-only price block for a non-home ship-to region. Both the
// The RRP leg takes the destination VAT + destination KS (the end customer pays
// the coffee tax); the wholesale leg takes the origin (home) VAT with NO
// destination KS — BRG lodges the Kaffeesteuer on the roaster's behalf, so the
// wholesale invoice is identical across destinations.
const DerivedRegionSection = ({
  breakdowns,
  name,
  homeName,
  msrpLabel,
  wholesaleLabel,
  t,
}: {
  breakdowns: RegionPriceBreakdowns;
  name: string;
  homeName: string;
  msrpLabel: string;
  wholesaleLabel: string;
  t: TFunction;
}) => {
  // The RRP leg carries the destination's KS, so a non-zero KS there means this
  // destination levies coffee tax — which BRG lodges on the wholesale side.
  const destinationLeviesKs = breakdowns.msrp.ks > 0;
  return (
    <div className="space-y-2" data-testid={setPriceUpdatePopoverTestId(`derived-region-${breakdowns.region}`)}>
      <div className="flex flex-wrap items-center gap-2">
        <CountryFlag countryCode={breakdowns.region} />
        <h4 className="text-sm font-medium">{name}</h4>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] font-normal">
            {t("products.priceUpdate.crossBorder.autoCalculated")}
          </Badge>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {t("products.priceUpdate.crossBorder.derivedRrpVat", { vat: vatRateToPercent(breakdowns.msrpVatRate) })}
          </Badge>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {destinationLeviesKs
              ? t("products.priceUpdate.crossBorder.derivedWholesaleBrg")
              : t("products.priceUpdate.crossBorder.derivedWholesaleVat", { vat: vatRateToPercent(breakdowns.wholesaleVatRate) })}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{msrpLabel}</p>
          <Input
            type="number"
            value={formatMoney(breakdowns.msrp.total)}
            readOnly
            disabled
            testId={setPriceUpdatePopoverTestId(`derived-msrp-${breakdowns.region}`)}
          />
          <CrossBorderBreakdown
            breakdown={breakdowns.msrp}
            vatRate={breakdowns.msrpVatRate}
            field="msrp"
            getTestId={(part) => setPriceUpdatePopoverTestId(`derived-msrp-${part}-${breakdowns.region}`)}
          />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{wholesaleLabel}</p>
          <Input
            type="number"
            value={formatMoney(breakdowns.wholesale.total)}
            readOnly
            disabled
            testId={setPriceUpdatePopoverTestId(`derived-wholesale-${breakdowns.region}`)}
          />
          <CrossBorderBreakdown
            breakdown={breakdowns.wholesale}
            vatRate={breakdowns.wholesaleVatRate}
            field="wholesalePrice"
            getTestId={(part) => setPriceUpdatePopoverTestId(`derived-wholesale-${part}-${breakdowns.region}`)}
            note={destinationLeviesKs ? t("products.priceUpdate.crossBorder.brgLodgesNote", { home: homeName }) : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export function PriceUpdatePopover({
  variant,
  onUpdatePrice,
  isUpdating,
  children,
  testId,
  open: controlledOpen,
  onOpenChange,
  onFormReady,
  ratesByRegion = DEFAULT_TAX_RATES_BY_REGION,
  marginFloorPercent,
}: Readonly<PriceUpdatePopoverProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;

  const handleOpenChange = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const preventClose = useCallback((e: Event) => e.preventDefault(), []);
  const validEffectiveDate = React.useMemo(() => getNextValidEffectiveDate(new Date()), []);

  // Cross-border setup: >1 ship-to country → home region editable + others
  // derived; OR DE-domestic → home-only breakdown, no derived
  // sections; otherwise the classic single-region price form.
  const rawShipTo = useMemo<CrossBorderCountries[]>(() => variant.shipTo ?? [], [variant.shipTo]);
  const { shipTo, showRegionPricingUi } = useMemo(
    () => resolveHomeRegionPricingContext(variant.homeRegion, rawShipTo),
    [variant.homeRegion, rawShipTo]
  );
  const homeRegion: CrossBorderCountries | undefined = useMemo(() => {
    if (!showRegionPricingUi) return undefined;
    return variant.homeRegion && shipTo.includes(variant.homeRegion) ? variant.homeRegion : shipTo[0];
  }, [showRegionPricingUi, variant.homeRegion, shipTo]);
  const weightKg = useMemo(() => parseBagSizeToKg(variant.size), [variant.size]);

  // The stored wholesale is KS-excluded (netto + VAT). Reconstruct the
  // coffee-tax-inclusive bruto so the roaster edits the same gross they entered
  // at import; submit drops the KS again (see breakdownsToSkuPrices). A no-op for
  // non-cross-border SKUs (no home region) and KS-free regions (e.g. NL).
  const defaultWholesale = useMemo(
    () => displayWholesaleForRegion(variant.wholesalePrice, homeRegion, ratesByRegion, weightKg),
    [variant.wholesalePrice, homeRegion, ratesByRegion, weightKg]
  );

  const schema = useMemo(() => createSchema(t, validEffectiveDate, marginFloorPercent), [validEffectiveDate, t, marginFloorPercent]);

  const buildDefaultValues = useCallback(
    () => ({
      msrp: variant.msrp,
      wholesalePrice: defaultWholesale,
      effectiveDate: validEffectiveDate,
      notes: "",
    }),
    [variant.msrp, defaultWholesale, validEffectiveDate]
  );

  const form = useForm<PriceUpdateFormInputValues, unknown, PriceUpdateFormValues>({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: buildDefaultValues(),
  });

  React.useEffect(() => {
    onFormReady?.(form);
    // onFormReady is stable (defined once at story level); form is stable per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (open) {
      form.reset(buildDefaultValues());
      void form.trigger();
    }
    // form.reset/trigger are stable RHF refs; including `form` would re-fire on
    // every onChange (RHF rebuilds the form object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, buildDefaultValues]);

  const watchedMsrp = useWatch({ control: form.control, name: "msrp" });
  const watchedWholesale = useWatch({ control: form.control, name: "wholesalePrice" });

  // Guards against submitting a no-op request: the roaster must actually move
  // the price away from what's shown by default, not just re-enter the same
  // figures with different notes/date. Compares against defaultWholesale (the
  // displayed bruto default), not variant.wholesalePrice, since cross-border
  // reconstructs a KS-inclusive default that differs from the stored netto price.
  // Rounded to the nearest cent on both sides: the pipeline rounds >2-decimal
  // input before calculating, so e.g. re-entering "5.423" over a current 5.42
  // sends the same 5.42 and must NOT count as a change.
  const hasPriceChanged =
    roundMoney(asNumber(watchedMsrp)) !== roundMoney(variant.msrp) ||
    roundMoney(asNumber(watchedWholesale)) !== roundMoney(defaultWholesale);

  // Live cross-border pricing: build per-region Netto/KS/VAT/Final breakdowns
  // from the entered home-region gross prices. Null for the classic flow.
  const pricing = useMemo(() => {
    if (!homeRegion) return null;
    const breakdowns = computeRegionPriceBreakdowns({
      msrp: asNumber(watchedMsrp),
      wholesalePrice: asNumber(watchedWholesale),
      flags: GROSS_FLAGS,
      homeRegion,
      shipTo,
      weightKg,
      ratesByRegion,
    });
    const home = breakdowns.find((entry) => entry.region === homeRegion);
    if (!home) return null;
    const derived = breakdowns.filter((entry) => entry.region !== homeRegion);
    // RRP keeps its KS-inclusive gross; wholesale is sent KS-excluded (netto + VAT).
    const skuPrices = breakdownsToSkuPrices(breakdowns, asNumber(watchedWholesale), homeRegion);
    return { home, derived, skuPrices };
  }, [homeRegion, watchedMsrp, watchedWholesale, ratesByRegion, weightKg, shipTo]);

  // Live margin check — bypasses onBlur so the error border appears on each keystroke.
  const marginFloorError = useMemo(() => {
    if (!marginFloorPercent || marginFloorPercent <= 0) return null;
    const msrp = asNumber(watchedMsrp);
    const wholesale = asNumber(watchedWholesale);
    if (msrp <= 0 || wholesale <= 0) return null;
    const margin = calculateMarginPercent(msrp, wholesale);
    return margin < marginFloorPercent
      ? t("products.priceUpdate.validation.marginTooLow", { percent: marginFloorPercent, current: margin.toFixed(1) })
      : null;
  }, [marginFloorPercent, watchedMsrp, watchedWholesale, t]);

  React.useEffect(() => {
    if (!marginFloorPercent || marginFloorError !== null) return;
    const error = form.getFieldState("wholesalePrice").error;
    if (error?.type === "custom") form.clearErrors("wholesalePrice");
  }, [marginFloorError, marginFloorPercent, form]);

  const onSubmit = async (values: PriceUpdateFormValues) => {
    // Existing weekly inventory rides on every ship-to entry: the backend
    // rejects a price update whose roaster-country entry omits it.
    const fallbackWeeklyInventory = variant.weeklyInventory;
    const currentByCountry = buildShipToCurrentValues(shipTo, variant.shipToInfo, variant.roasterCountryCode, {
      msrp: variant.msrp,
      wholesalePrice: variant.wholesalePrice,
      weeklyInventory: fallbackWeeklyInventory,
    });
    const currentFor = (country: string) => currentByCountry.find((entry) => entry.countryCode === country);

    // Cross-border (or DE-domestic) sends one entry per ship-to
    // region carrying that region's derived new prices (KS-excluded wholesale)
    // and its current prices; the classic flow sends a single entry for the
    // roaster's own country built straight from the entered values.
    const shipToCountries: CountryApprovalValues[] =
      showRegionPricingUi && pricing
        ? pricing.skuPrices.map((sku) => {
            const current = currentFor(sku.region);
            return {
              shipToCountryCode: sku.region,
              MSRP: sku.msrp,
              currentMSRP: current?.currentMsrp ?? variant.msrp,
              wholesalePrice: sku.wholesalePrice,
              currentWholesalePrice: current?.currentWholesalePrice ?? variant.wholesalePrice,
              weeklyInventory: String(current?.currentWeeklyInventory ?? fallbackWeeklyInventory ?? 0),
              webStatus: current?.currentWebStatus,
            };
          })
        : [
            {
              shipToCountryCode: variant.roasterCountryCode,
              MSRP: values.msrp ?? variant.msrp,
              currentMSRP: variant.msrp,
              wholesalePrice: values.wholesalePrice ?? variant.wholesalePrice,
              currentWholesalePrice: variant.wholesalePrice,
              // The classic branch must consult currentByCountry too: roasters
              // outside the cross-border regions (e.g. GB) have no home region,
              // so they NEVER take the branch above, yet their per-channel
              // shipToInfo still carries the approved weekly inventory. The
              // variant's flat figure is the staler Salesforce-overlaid one, so
              // it stays a fallback for SKUs with no matching shipToInfo entry.
              weeklyInventory: String(currentFor(variant.roasterCountryCode)?.currentWeeklyInventory ?? fallbackWeeklyInventory ?? 0),
              webStatus: currentFor(variant.roasterCountryCode)?.currentWebStatus,
            },
          ];

    const request: RoasterApprovalRequestDraft = {
      actionType: "Price Update",
      vendorKey: variant.vendorKey,
      roasterCountryCode: variant.roasterCountryCode,
      itemNumbers: [variant.sku],
      roasterComments: values.notes,
      coffeeTerm: variant.coffeeTerm,
      productName: variant.productName,
      roasterEffectiveDate: format(values.effectiveDate, "yyyy-MM-dd"),
      shipToCountries,
    };

    try {
      const results = await onUpdatePrice(request);
      handleOpenChange(false);
      toast({
        title: t("products.priceUpdate.submitSuccess"),
        description:
          results.length > 0 ? (
            <span>
              <span>{t("products.approvalRequest.submitSuccessDetail")}</span>
              <ApprovalRequestResults results={results} />
            </span>
          ) : undefined,
        variant: "success",
      });
    } catch (error) {
      if (error instanceof SessionEndedError) return;
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("products.priceUpdate.submitError"),
        variant: "destructive",
      });
    }
  };

  const disableUnavailableDates = (date: Date): boolean => !isSameCalendarDate(date, validEffectiveDate);

  const msrpLabel = t("products.priceUpdate.msrp");
  const wholesaleLabel = t("products.priceUpdate.wholesalePrice");
  const errors = form.formState.errors;
  const homeRates = homeRegion ? ratesByRegion[homeRegion] : undefined;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        testId={testId ?? setPriceUpdatePopoverTestId("content")}
        // Cap to the space Radix has between the trigger and the viewport edge
        // (it flips side for collisions), so the popover never runs off the top
        // or bottom of the screen regardless of which row's edit button opened
        // it. The body scrolls inside; the footer stays pinned.
        className="flex max-h-[var(--radix-popover-content-available-height)] w-96 flex-col p-0"
        align="start"
        collisionPadding={8}
        onInteractOutside={preventClose}
        onEscapeKeyDown={preventClose}
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          {/* Scrollable body: the cross-border layout can exceed the viewport,
              so the fields scroll while the action footer stays pinned. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {showRegionPricingUi && homeRegion && homeRates && pricing ? (
              <>
                <div className="space-y-3" data-testid={setPriceUpdatePopoverTestId(`home-region-${homeRegion}`)}>
                  <div className="flex flex-wrap items-center gap-2">
                    <CountryFlag countryCode={homeRegion} />
                    <h4 className="text-sm font-medium">
                      {t("products.priceUpdate.shipToHeading", { country: countryName(t, homeRegion) })}
                    </h4>
                    <div className="ml-auto flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant="secondary"
                        className="text-[10px] font-normal"
                        testId={setPriceUpdatePopoverTestId(`home-ks-badge-${homeRegion}`)}
                      >
                        {homeRates.ksPerKg > 0
                          ? t("products.priceUpdate.crossBorder.ksBadge", { ks: formatEuro(homeRates.ksPerKg) })
                          : t("products.priceUpdate.crossBorder.noKsBadge")}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] font-normal">
                        {t("products.priceUpdate.crossBorder.vatBadge", { vat: vatRateToPercent(homeRates.vatRate) })}
                      </Badge>
                    </div>
                  </div>
                  <PriceInput
                    control={form.control}
                    name="msrp"
                    label={msrpLabel}
                    error={errors.msrp?.message}
                    hasMarginError={!!marginFloorError}
                  >
                    <CrossBorderBreakdown
                      breakdown={pricing.home.msrp}
                      vatRate={pricing.home.msrpVatRate}
                      field="msrp"
                      getTestId={(part) => setPriceUpdatePopoverTestId(`home-msrp-${part}-${homeRegion}`)}
                    />
                  </PriceInput>
                  <PriceInput
                    control={form.control}
                    name="wholesalePrice"
                    label={wholesaleLabel}
                    error={marginFloorError ?? errors.wholesalePrice?.message}
                    hasMarginError={!!marginFloorError}
                  >
                    <CrossBorderBreakdown
                      breakdown={pricing.home.wholesale}
                      vatRate={pricing.home.wholesaleVatRate}
                      field="wholesalePrice"
                      getTestId={(part) => setPriceUpdatePopoverTestId(`home-wholesalePrice-${part}-${homeRegion}`)}
                    />
                  </PriceInput>
                </div>
                {pricing.derived.map((entry) => (
                  <DerivedRegionSection
                    key={entry.region}
                    breakdowns={entry}
                    name={t("products.priceUpdate.shipToHeading", { country: countryName(t, entry.region) })}
                    homeName={countryName(t, homeRegion)}
                    msrpLabel={msrpLabel}
                    wholesaleLabel={wholesaleLabel}
                    t={t}
                  />
                ))}
                <CrossBorderNote homeLeviesKs={homeRates.ksPerKg > 0} testId={setPriceUpdatePopoverTestId("cross-border-note")} />
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">{t("products.priceUpdate.title")}</h4>
                  <p className="text-xs text-muted-foreground">{t("products.priceUpdate.description", { size: variant.size })}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <PriceInput
                    control={form.control}
                    name="msrp"
                    label={msrpLabel}
                    error={errors.msrp?.message}
                    hasMarginError={!!marginFloorError}
                  />
                  <PriceInput
                    control={form.control}
                    name="wholesalePrice"
                    label={wholesaleLabel}
                    error={marginFloorError ?? errors.wholesalePrice?.message}
                    hasMarginError={!!marginFloorError}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-xs">{t("products.priceUpdate.effectiveDate")}</Label>
              <Controller
                control={form.control}
                name="effectiveDate"
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start text-left font-normal bg-transparent">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, "PPP") : ""}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          if (date) field.onChange(date);
                        }}
                        disabled={disableUnavailableDates}
                        month={validEffectiveDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              <p className="text-xs text-muted-foreground">{t("products.priceUpdate.firstOfMonthOnly")}</p>
              {form.formState.errors.effectiveDate && (
                <p className="text-xs text-destructive">{form.formState.errors.effectiveDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priceNotes" className="text-xs">
                {t("products.priceUpdate.notes")}
              </Label>
              <Textarea id="priceNotes" placeholder={t("products.priceUpdate.notesPlaceholder")} {...form.register("notes")} rows={3} />
              {form.formState.errors.notes && <p className="text-xs text-destructive">{form.formState.errors.notes.message}</p>}
            </div>
          </div>

          {/* Pinned footer: stays visible while the body scrolls. */}
          <div className="flex gap-2 border-t border-border bg-popover p-4">
            <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)} className="flex-1">
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!form.formState.isValid || !hasPriceChanged || isUpdating}
              className="flex-1 bg-primary"
            >
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUpdating ? t("products.priceUpdate.submitting") : t("products.priceUpdate.submitForApproval")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
